import { createApp, ref, type Component } from "vue";

import { SiteType } from "@appEnum/SiteType";
import { isContentDesignerFrame } from "@compUtility/content/designer/contentDesignerProtocol";
import { NativeAppMessageType, postToNativeShell } from "@utility/nativeAppBridge";
import toast from "@utility/toastUtils";
import { Guid } from "guid-typescript";
import { getActivePinia } from "pinia";
import { useAppStore } from "../stores/appStore";
import { activityTracker } from "./activityTracker";
import { isNullOrWhiteSpace } from "./stringUtils";

const baseUrl: string = "/api/v1.0/";
const API_REFRESH_ENDPOINT = "APIKey/GetKey";
// The shell answers auth:unauthorized by reloading the WebView with a fresh token; if it doesn't, redirect.
const NATIVE_AUTH_RECOVERY_TIMEOUT_MS = 3000;
let APIKey: string;
let isFetchingApiKey: boolean = false;
let isRedirectingToLogin: boolean = false;
export const buildMismatchDetected = ref<boolean>(
  typeof window !== "undefined" && !!window["__buildMismatchDetected"]
);
export const previousBuild = ref<string | null>(null);
export const newBuild = ref<string | null>(null);
let currentBuildNumber: string | null = null;
type methodType = "get" | "post" | "put" | "delete";

/**
 * The app store, or undefined when no Vue app has created one yet.
 *
 * Not every caller of this module arrives from inside a component. The content-designer bootstrap and
 * the placement runtime both run from the entry bundle on DOMContentLoaded, which on a frontend-routed
 * page is before the master template's own handler calls startS1App -- and useAppStore() with no active
 * pinia throws reading `_s`, taking the whole call down with it.
 */
const tryUseAppStore = (): ReturnType<typeof useAppStore> | undefined =>
  getActivePinia() ? useAppStore() : undefined;

export interface IApiRequest<T> {
  endpoint?: string;
  data?: T;
  method: methodType;
  error?: Function;
  fullUri?: string;
}

export async function fetchDataRequest<T>(request: IApiRequest<T>): Promise<T> {
  return new Promise<T>(async (resolve, reject) =>
    resolve(
      (await fetchData(request.endpoint, request.data, request.error, request.method, request.fullUri)) ??
        ({} as T)
    )
  );
}

export async function fetchData<T>(
  endpoint?: string,
  data?: any,
  error?: Function,
  method: methodType = "post",
  fullUri?: string
): Promise<T | void> {
  APIKey = APIKey === undefined ? getApiKey() : APIKey;
  error = error || defaultErrorCallback;
  if (isFetchingApiKey && endpoint !== API_REFRESH_ENDPOINT) {
    return queueApiRequest<T>(Guid.create(), endpoint, data, error, method, fullUri);
  }
  if (!endpoint && !fullUri) {
    console.error("No endpoint or fullUri provided to fetchData");
    return;
  }

  const startTime = performance.now();
  const requestUrl = fullUri ? fullUri : `${baseUrl}${endpoint}`;

  return new Promise<T>(async (resolve, reject) => {
    let res;
    try {
      res = await fetch(addGlobalParams(`${requestUrl}`), {
        method: method,
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "X-ApiKey": APIKey,
        },
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "same-origin", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        body: data ? JSON.stringify(data) : "",
      });

      const duration = performance.now() - startTime;

      if (res.ok) {
        const serverBuild = res.headers.get("x-build-number");
        const appArgs = tryUseAppStore()?.appArgs;
        if (serverBuild && !currentBuildNumber) {
          currentBuildNumber = serverBuild;
        } else if (serverBuild && serverBuild !== currentBuildNumber && !buildMismatchDetected.value) {
          if (appArgs?.SiteTypeId === SiteType.S1) {
            previousBuild.value = currentBuildNumber;
            newBuild.value = serverBuild;
            buildMismatchDetected.value = true;
            window["__buildMismatchDetected"] = true;
            setTimeout(() => {
              toast.warning(
                `Build updated: ${previousBuild.value} → ${newBuild.value}.\n👉 Click to refresh.`,
                {
                  autoClose: false,
                  onClick: () => window.location.reload(),
                }
              );
            }, 2000);
          }
        }
        const responseData = await res.json();

        // Track successful API call
        activityTracker.trackApiCall({
          timestamp: new Date().toISOString(),
          endpoint: endpoint || fullUri || "unknown",
          method: method.toUpperCase(),
          requestData: sanitizeForLogging(data),
          responseData: sanitizeForLogging(responseData),
          status: res.status,
          duration: Math.round(duration),
        });

        resolve(responseData);
        return;
      }

      if (res.status === 403) {
        await refreshApiKey(Guid.create(), resolve, res, endpoint!, data, error);
        return;
      }

      // Track failed API call
      activityTracker.trackApiCall({
        timestamp: new Date().toISOString(),
        endpoint: endpoint || fullUri || "unknown",
        method: method.toUpperCase(),
        requestData: sanitizeForLogging(data),
        status: res.status,
        error: res.statusText,
        duration: Math.round(duration),
      });

      // create error instance with HTTP status text
      const newError = new Error(res.statusText);
      throw newError;
    } catch (err) {
      console.error(`Error in fetchData: ${err}`);

      // Track error
      const duration = performance.now() - startTime;
      activityTracker.trackApiCall({
        timestamp: new Date().toISOString(),
        endpoint: endpoint || fullUri || "unknown",
        method: method.toUpperCase(),
        requestData: sanitizeForLogging(data),
        error: err instanceof Error ? err.message : String(err),
        duration: Math.round(duration),
      });

      if (error) {
        reject(error(err, res, endpoint, data));
      } else {
        reject(err);
      }
    }
  });
}

// ── File Upload ───────────────────────────────────────────────────────────────

/**
 * Sends a multipart/form-data POST request to the given API endpoint and returns the parsed JSON response.
 *
 * Use this instead of `fetchData` when uploading files. The browser sets the `Content-Type` header
 * (including the multipart boundary) automatically — do not set it manually or the boundary will be missing.
 *
 * Unlike `fetchData`, this function does not queue requests while an API key refresh is in flight.
 * For file uploads this is acceptable; if the key happens to expire mid-upload the request will fail
 * and the user can retry.
 *
 * @param endpoint - API path relative to `/api/v1.0/` (e.g. `"AcquisitionImport/parse-csv-import"`)
 * @param formData - The FormData body to send. Append scalar fields with `String(value)` and files as `Blob`.
 * @param error - Optional error callback. Defaults to the global error modal handler.
 */
export async function fetchFormData<T>(
  endpoint: string,
  formData: FormData,
  error?: Function
): Promise<T | void> {
  APIKey = APIKey === undefined ? getApiKey() : APIKey;
  error = error || defaultErrorCallback;

  const startTime = performance.now();
  const requestUrl = addGlobalParams(`${baseUrl}${endpoint}`);

  return new Promise<T>(async (resolve, reject) => {
    let res: Response | undefined;
    try {
      res = await fetch(requestUrl, {
        method: "post",
        cache: "no-cache",
        headers: { "X-ApiKey": APIKey },
        redirect: "follow",
        referrerPolicy: "same-origin",
        body: formData,
      });

      const duration = performance.now() - startTime;

      if (res.ok) {
        const responseData = await res.json();
        activityTracker.trackApiCall({
          timestamp: new Date().toISOString(),
          endpoint,
          method: "POST",
          status: res.status,
          duration: Math.round(duration),
        });
        resolve(responseData);
        return;
      }

      activityTracker.trackApiCall({
        timestamp: new Date().toISOString(),
        endpoint,
        method: "POST",
        status: res.status,
        error: res.statusText,
        duration: Math.round(duration),
      });
      throw new Error(res.statusText);
    } catch (err) {
      console.error(`Error in fetchFormData: ${err}`);
      const duration = performance.now() - startTime;
      activityTracker.trackApiCall({
        timestamp: new Date().toISOString(),
        endpoint,
        method: "POST",
        error: err instanceof Error ? err.message : String(err),
        duration: Math.round(duration),
      });
      if (error) {
        reject(error(err, res, endpoint, formData));
      } else {
        reject(err);
      }
    }
  });
}

const refreshApiKey = <T>(
  requestId: Guid,
  promiseSuccess: Function,
  apiFailedResp: Response,
  endpoint: string,
  data?: any,
  error?: Function
): void => {
  apiFailedResp.text().then((t) => {
    if (t && t.trim().toLowerCase() === "invalid or expired api key ('x-apikey' http header)") {
      isFetchingApiKey = true;
      console.info(`Api key expired, queuing api request ${requestId} while new key is fetched`);
      queueApiRequest<T>(requestId, endpoint, data, error).then((x) => {
        if (x) {
          promiseSuccess(x);
        } else if (error) {
          error({ status: 500 }, endpoint, data);
        }
      });
      console.info("Refreshing api key...");
      fetchData<{ apiKey: string }>(API_REFRESH_ENDPOINT, {}, function (res) {
        console.error(`Could not refresh API key: ${res}`);
      }).then((keyResp) => {
        if (keyResp?.apiKey?.trim() !== "") {
          console.info("Api key refreshed.");
          APIKey = keyResp?.apiKey!;

          //backward compat:
          if ((<any>window).hana) {
            (<any>window).hana.webservices.APIKey = APIKey;
          }

          if ((<any>window).cw) {
            (<any>window).cw.webservices.APIKey = APIKey;
          }
        }
        isFetchingApiKey = false;
      });
    }
  });
};

async function queueApiRequest<T>(
  requestId: Guid,
  endpoint?: string,
  data?: any,
  error?: Function,
  method: methodType = "post",
  fullUri?: string
): Promise<T | void> {
  return new Promise<T>(async (resolve, reject) => {
    const retry = () => {
      setTimeout(() => {
        if (isFetchingApiKey) {
          retry();
        } else {
          console.info(`Reprocessing queued api-request ${requestId}...`);
          fetchData<T>(endpoint, data, error, method, fullUri)
            .then((d) => {
              if (d) {
                console.info(`Resolved api-request ${requestId}.`);
                resolve(d);
              } else {
                console.warn(`Resolving api-request ${requestId} resulted in no result.`);
              }
            })
            .catch((err) => {
              console.warn(`Error resolving api-request ${requestId}.`);
              reject(err);
            });
        }
      }, 100);
    };

    retry();
  });
}

/**
 * Sends a GET request to the given API endpoint and returns the raw `Response` object for streaming binary data.
 *
 * Use this instead of `fetchData` when downloading files (PDFs, CSVs, etc.) where you need the raw response
 * rather than parsed JSON. The caller is responsible for reading the response body (e.g. via `response.blob()`).
 *
 * Retries once if the API key has expired (403 with the standard expiry message).
 * Returns `null` if the request fails or the key cannot be refreshed.
 *
 * @param endpoint - API path relative to `/api/v1.0/` (e.g. `"AcquisitionImport/download-ar-import-file/1"`)
 */
export async function fetchBlob(endpoint: string): Promise<Response | null> {
  APIKey = APIKey === undefined ? getApiKey() : APIKey;

  const doRequest = async (): Promise<Response> => {
    const url = addGlobalParams(`${baseUrl}${endpoint}`);
    return fetch(url, {
      method: "get",
      cache: "no-cache",
      headers: { "X-ApiKey": APIKey },
      redirect: "follow",
      referrerPolicy: "same-origin",
    });
  };

  let res = await doRequest();

  if (res.ok) {
    return res;
  }

  if (res.status === 403) {
    const text = await res.text();
    if (text.trim().toLowerCase() === "invalid or expired api key ('x-apikey' http header)") {
      const keyResp = await fetchData<{ apiKey: string }>(API_REFRESH_ENDPOINT, {});
      if (keyResp?.apiKey?.trim()) {
        APIKey = keyResp.apiKey;
        if ((window as any).hana) {
          (window as any).hana.webservices.APIKey = APIKey;
        }
        if ((window as any).cw) {
          (window as any).cw.webservices.APIKey = APIKey;
        }
        res = await doRequest();
        return res.ok ? res : null;
      }
    }
  }

  return null;
}

/**
 * Downloads a file from the given API endpoint and hands it to the browser to save.
 *
 * @param endpoint - API path relative to `/api/v1.0/`, served as a GET that returns the file.
 * @param fileName - The name the browser saves it under.
 * @returns `true` once the save has been started, `false` when the request failed.
 */
export async function downloadFile(endpoint: string, fileName: string): Promise<boolean> {
  const res = await fetchBlob(endpoint);
  if (!res) {
    return false;
  }

  const url = URL.createObjectURL(await res.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
  return true;
}

/**
 * Sends a POST request to the given API endpoint and returns the raw `Response` so the caller can read
 * the body incrementally (the server-sent-event streams behind the AI endpoints).
 *
 * Use this instead of `fetchData` when the response arrives in frames: `fetchData` awaits `res.json()`,
 * which buffers the whole body and defeats streaming. The caller owns the body — read it through
 * `response.body.getReader()` — and owns its own status handling, so no error modal is raised here.
 *
 * Retries once if the API key has expired (403 with the standard expiry message), so a long-lived
 * chat panel survives a key rollover.
 *
 * @param endpoint - API path relative to `/api/v1.0/` (e.g. `"AiAssist/chat-stream"`)
 * @param data - Request body, serialized as JSON.
 * @param signal - Optional AbortSignal so the caller can stop an in-flight stream.
 */
export async function fetchStream(endpoint: string, data?: any, signal?: AbortSignal): Promise<Response> {
  APIKey = APIKey === undefined ? getApiKey() : APIKey;

  const startTime = performance.now();

  const doRequest = async (): Promise<Response> =>
    fetch(addGlobalParams(`${baseUrl}${endpoint}`), {
      method: "post",
      cache: "no-cache",
      credentials: "include",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-ApiKey": APIKey,
      },
      redirect: "follow",
      referrerPolicy: "same-origin",
      body: data ? JSON.stringify(data) : "",
      signal,
    });

  let res = await doRequest();

  if (res.status === 403) {
    const text = await res.clone().text();
    if (text.trim().toLowerCase() === "invalid or expired api key ('x-apikey' http header)") {
      const keyResp = await fetchData<{ apiKey: string }>(API_REFRESH_ENDPOINT, {});
      if (keyResp?.apiKey?.trim()) {
        APIKey = keyResp.apiKey;
        if ((window as any).hana) {
          (window as any).hana.webservices.APIKey = APIKey;
        }
        if ((window as any).cw) {
          (window as any).cw.webservices.APIKey = APIKey;
        }
        res = await doRequest();
      }
    }
  }

  activityTracker.trackApiCall({
    timestamp: new Date().toISOString(),
    endpoint,
    method: "POST",
    status: res.status,
    error: res.ok ? undefined : res.statusText,
    duration: Math.round(performance.now() - startTime),
  });

  return res;
}

const getApiKey = (): string => {
  const hana = (<any>window).hana;
  const cw = (<any>window).cw;
  const appArgs = tryUseAppStore()?.appArgs;
  if (appArgs?.ApiKey?.trim() ?? "" !== "") {
    const kee = appArgs?.ApiKey?.trim() ?? "";
    if (hana) {
      hana.webservices.APIKey = kee;
    } else if (cw) {
      cw.webservices.APIKey = kee;
    }
    return kee;
  } else if (hana) {
    return hana.webservices.APIKey;
  } else if (cw) {
    return cw.webservices.APIKey;
  }
  return "invalid or expired api key ('x-apikey' http header)";
};

export const redirectToLogin = (url: string): void => {
  isRedirectingToLogin = true;
  window.location.href = url;
};

export const defaultErrorCallback = (
  error: unknown,
  response: Response | undefined,
  endpoint: string,
  data: unknown
): void => {
  let title = "An error occurred";
  let body = "An unknown error occurred while attempting the last operation.";

  switch (response?.status ?? 500) {
    case 400:
      title = "Bad Data";
      body =
        "A malformed or incomplete request was sent to the server and could not be serviced.  If you think this message is in error please contact your system administrator and detail the steps you took to get this message.";
      break;
    case 401:
      {
        const appStore = tryUseAppStore();
        const isS1 = appStore?.isInternal() ?? false;
        const isCustPortal = appStore?.isCustomerPortal ?? false;

        // The designer frames pages the content manager is not a signed-in visitor of, so /account and
        // friends answer 401 exactly as designed. Navigating the frame to /login would replace the page
        // being edited, and a dialog stacked behind the designer chrome is unreachable -- neither is a
        // useful answer when the point is to pick a selector on the page shell.
        if (isContentDesignerFrame()) {
          return;
        }

        if (isCustPortal) {
          if (!isRedirectingToLogin) {
            isRedirectingToLogin = true;
            const route = window.location.pathname + window.location.search;
            const loginUrl = `/login?redirect=${encodeURIComponent(route)}`;

            if (postToNativeShell(NativeAppMessageType.AuthUnauthorized, { route })) {
              setTimeout(() => redirectToLogin(loginUrl), NATIVE_AUTH_RECOVERY_TIMEOUT_MS);
            } else {
              redirectToLogin(loginUrl);
            }
          }
          return;
        }
        // Nobody is logged in (login page, or a probe that fired before auth): 401 is the correct
        // answer and the dialog is pure noise. Session-expiry for a logged-in user still surfaces.
        if (isS1 && (appStore?.appArgs?.CurrentUserObj?.UserId ?? 0) <= 0) {
          return;
        }

        title = "Access denied";
        body =
          'You do not currently have access to the requested resource. If you have not yet logged on or your session has expired, please <a href="/login">login</a>. If you are logged in and think this message is in error please contact your system administrator.';
      }
      break;
    case 404:
      title = "Not found";
      body =
        "The requested resource cannot be found. If you think this message is in error please contact your system administrator and detail the steps you took to get this message.";
      break;
  }

  if (document.getElementsByClassName("environment-indicator").length > 0) {
    body += `
      <details class="mt15">
        <summary><small>Debug Info</small></summary>
        <div class="pl20"><small><b>Endpoint:</b> ${endpoint}</small></div>
        <div class="pl20"><small><b>Response Status:</b> ${response?.status ?? "N/A"}</small></div>
        <div class="pl20"><small><b>Response Text:</b> ${!isNullOrWhiteSpace(response?.statusText) ? response?.statusText : "N/A"}</small></div>
        <div class="pl20"><small><b>Data:</b> ${JSON.stringify(sanitizeForLogging(data))}</small></div>
      </details>`;
  }

  void showErrorModal(title, body);
};

const hasFlushCacheParam = (): boolean =>
  !isNullOrWhiteSpace(new URLSearchParams(window.location.search).get("flushCache"));

export const hasHideHubspotChatParam = (): boolean =>
  !isNullOrWhiteSpace(new URLSearchParams(window.location.search).get("hideHubspotChat"));

export const isTestingSite = (): boolean => {
  const url = window.location.href;
  const regex = /^https:\/\/(?:localdev|staging|shipleyone.local|s1staging)/;
  return regex.test(url);
};

const addGlobalParams = (url: string): string => {
  if (hasFlushCacheParam()) {
    return `${url}${url.includes("?") ? "&" : "?"}flushCache=1`;
  }

  return url;
};

// modal.vue is loaded on demand, not imported. A static import here closes an import cycle
// (webServices -> modal -> actionButton -> textInput -> appStore -> webServices) whose members
// then initialise in whatever order the first chunk to reach them dictates. Async chunks that
// enter the cycle from a new edge -- the CKEditor image-upload adapter reaching the asset store,
// for one -- hit the half-built binding and throw a TDZ ReferenceError at module scope.
const showErrorModal = async (title: string, body: string): Promise<void> => {
  // Prefetched, because this is the component that reports a failed request: it needs to be warm
  // in the browser cache before the first failure, not fetched in the middle of one.
  let Modal: Component;
  try {
    Modal = (await import(/* webpackPrefetch: true */ "@compUtility/modal.vue")).default;
  } catch {
    // Nothing is reaching the network. A toast still says so without needing another chunk.
    toast.error(`${title}. ${body.replace(/<[^>]*>/g, " ").trim()}`);
    return;
  }

  let elem = document.querySelector(".modal-mount");
  if (elem) {
    elem.remove();
  }
  elem = document.createElement("div");
  const newModalId: any = Guid.create();
  const modalMount = `modal-mount-${newModalId}`;
  elem.classList.add(modalMount);
  elem.classList.add("modal-mount");
  document.body.appendChild(elem);

  const app = createApp(Modal, {
    onMounted: () => {
      //close the old loading indicator if it's still open
      const legLib = (<any>window).hana ?? (<any>window).cw;
      if (legLib && legLib.loadingIndicator && typeof legLib.loadingIndicator.stop === "function") {
        legLib.loadingIndicator.stop();
      }
    },
    onModalClosed: () => {
      app.unmount();
      if (elem) {
        elem.remove();
      }
    },
    title: title,
    bodyContent: body,
    // The error modal must never load the tool menu. enableModalTools defaults to true, and tool
    // discovery issues an authenticated rights call -- so a 401 would render this modal, whose
    // own probe 401s, which renders another modal, until the tab dies.
    enableModalTools: "false",
  });
  //this prevents a warning when this throws because it's not registered as a full app
  app.directive("analytic-push", {});
  app.mount(`.${modalMount}`);
};

/**
 * Sanitize data for logging - remove sensitive information and limit size
 */
const sanitizeForLogging = (data: any): any => {
  if (!data) return data;

  try {
    // Convert to string and limit size
    let sanitized = JSON.parse(JSON.stringify(data));

    // Remove common sensitive fields
    const sensitiveKeys = [
      "password",
      "apikey",
      "api_key",
      "token",
      "authorization",
      "ssn",
      "socialsecuritynumber",
      "creditcard",
      "cvv",
      "privatekey",
      "passphrase",
    ];
    const privateKeyPattern = /-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----/;

    const removeSensitiveData = (obj: any): any => {
      if (typeof obj === "string" && privateKeyPattern.test(obj)) {
        return "[REDACTED]";
      }
      if (typeof obj !== "object" || obj === null) return obj;

      if (Array.isArray(obj)) {
        // Limit array size
        return obj.slice(0, 5).map(removeSensitiveData);
      }

      const result: any = {};
      for (const key in obj) {
        if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk))) {
          result[key] = "[REDACTED]";
        } else {
          result[key] = removeSensitiveData(obj[key]);
        }
      }
      return result;
    };

    sanitized = removeSensitiveData(sanitized);

    return truncateJsonForLogging(sanitized, 2000);
  } catch (err) {
    return "[Unable to serialize]";
  }
};

const truncateJsonForLogging = (value: any, maxLength: number): any => {
  try {
    const str = JSON.stringify(value);
    if (str.length <= maxLength) {
      return value;
    }
    return {
      __truncated: true,
      __originalLength: str.length,
      __preview: str.slice(0, maxLength),
    };
  } catch {
    return "[Unable to serialize]";
  }
};
